<template>
  <div class="ai-cover-generator">
    <button class="btn ai-cover-trigger" type="button" @click="openModal">
      <span class="btn-inner--text">
        <i class="uil-rocket ai-cover-trigger-icon"></i>
        {{ $t("general.generate_with_ai") }}
      </span>
    </button>

    <div v-if="isOpen" class="ai-cover-modal-backdrop" @click.self="closeModal">
      <div class="ai-cover-modal" role="dialog" aria-modal="true">
        <div class="ai-cover-modal-header">
          <h4>{{ $t("general.generate_with_ai") }}</h4>
          <button class="ai-cover-close" type="button" @click="closeModal">
            <i class="uil-times"></i>
          </button>
        </div>

        <div class="ai-cover-modal-body">
          <label class="form-label" for="ai-cover-prompt">{{ $t("general.ai_image_prompt") }}</label>
          <textarea
              id="ai-cover-prompt"
              v-model="prompt"
              class="form-control"
              maxlength="500"
              rows="4"
              :placeholder="$t('general.ai_image_prompt_placeholder')"
          ></textarea>

          <div v-if="errorMessage" class="alert alert-danger mt-3 mb-0">
            <i class="uil-exclamation-triangle mr-1"></i>
            {{ errorMessage }}
          </div>

          <div v-if="isGenerating" class="ai-cover-loading">
            {{ $t("general.ai_image_generating") }} <span class="dots"></span>
          </div>

          <div v-if="previewSource && !isGenerating" class="ai-cover-preview">
            <img :src="previewSource" alt=""/>
          </div>
        </div>

        <div class="ai-cover-modal-footer">
          <button class="btn btn-light" type="button" @click="closeModal">
            {{ $t("messages.cancel") }}
          </button>
          <button
              class="btn btn-primary"
              type="button"
              :disabled="isGenerating || isApplying || !canGenerate"
              @click="generateImage"
          >
            <span v-if="isGenerating" class="dots"></span>
            {{ previewSource ? $t("general.regenerate") : $t("general.generate_image") }}
          </button>
          <button
              v-if="previewSource"
              class="btn btn-success"
              type="button"
              :disabled="isGenerating || isApplying"
              @click="useImage"
          >
            {{ $t("general.use_this_image") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

const {
  createGeneratedImageFile,
  getFirstGeneratedImage,
  getGeneratedImagePreviewSource,
} = require("@/utils/aiCoverImageUtils");

export default {
  name: "AiCoverImageGenerator",
  props: {
    maxSizeMb: {
      type: Number,
      required: true,
    },
    filePrefix: {
      type: String,
      default: "ai-cover",
    },
  },
  data() {
    return {
      isOpen: false,
      isGenerating: false,
      isApplying: false,
      prompt: "",
      generatedImage: null,
      previewSource: "",
      errorMessage: "",
    };
  },
  computed: {
    canGenerate() {
      return !!this.prompt.trim();
    },
  },
  methods: {
    openModal() {
      this.isOpen = true;
      this.errorMessage = "";
    },
    closeModal() {
      if (this.isGenerating || this.isApplying) return;
      this.isOpen = false;
      this.generatedImage = null;
      this.previewSource = "";
      this.errorMessage = "";
    },
    resolveErrorMessage(error) {
      const errorKey = error && error.message;

      if (errorKey === "invalid_format") return this.$t("general.ai_image_invalid_format");
      if (errorKey === "size_exceeded") return this.$t("general.ai_image_size_exceeded");

      return error?.response?.data?.message || this.$t("general.ai_image_generation_failed");
    },
    async generateImage() {
      if (!this.canGenerate) return;

      this.isGenerating = true;
      this.errorMessage = "";
      this.generatedImage = null;
      this.previewSource = "";

      try {
        const response = await axios.post("/api/ai/storeImage", {
          image_subject: this.prompt.trim(),
          image_count: 1,
          image_response_format: "b64_json",
        });
        const image = getFirstGeneratedImage(response.data);

        if (!image) throw new Error("missing_image");

        this.generatedImage = image;
        this.previewSource = getGeneratedImagePreviewSource(image);
      } catch (error) {
        this.errorMessage = this.resolveErrorMessage(error);
      } finally {
        this.isGenerating = false;
      }
    },
    async useImage() {
      if (!this.generatedImage) return;

      this.isApplying = true;
      this.errorMessage = "";

      try {
        const file = await createGeneratedImageFile(this.generatedImage, {
          maxSizeMb: this.maxSizeMb,
          filePrefix: this.filePrefix,
        });
        const previewUrl = URL.createObjectURL(file);

        this.$emit("image-selected", { file, previewUrl });
        this.isOpen = false;
        this.generatedImage = null;
        this.previewSource = "";
      } catch (error) {
        this.errorMessage = this.resolveErrorMessage(error);
      } finally {
        this.isApplying = false;
      }
    },
  },
};
</script>

<style scoped>
.ai-cover-generator {
  margin-top: 12px;
  text-align: center;
}

.ai-cover-trigger {
  align-items: center;
  background: #0f9f83;
  border-color: #0f9f83;
  color: #fff;
  display: inline-flex;
  font-weight: 600;
  justify-content: center;
  position: static;
}

.ai-cover-trigger:hover,
.ai-cover-trigger:focus {
  background: #0b806a;
  border-color: #0b806a;
  color: #fff;
}

.ai-cover-trigger-icon {
  font-size: 16px;
  margin-right: 6px;
  vertical-align: -1px;
}

.ai-cover-modal-backdrop {
  align-items: center;
  background: rgba(15, 23, 42, 0.55);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  padding: 24px;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1000000;
}

.ai-cover-modal {
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.24);
  max-height: calc(100vh - 48px);
  max-width: 640px;
  overflow: auto;
  width: 100%;
}

.ai-cover-modal-header,
.ai-cover-modal-footer {
  align-items: center;
  display: flex;
  gap: 10px;
  padding: 18px 20px;
}

.ai-cover-modal-header {
  border-bottom: 1px solid #edf0f5;
  justify-content: space-between;
}

.ai-cover-modal-header h4 {
  font-size: 18px;
  line-height: 1.3;
  margin: 0;
}

.ai-cover-close {
  align-items: center;
  background: transparent;
  border: 0;
  color: #4b5563;
  display: inline-flex;
  font-size: 22px;
  height: 34px;
  justify-content: center;
  padding: 0;
  width: 34px;
}

.ai-cover-modal-body {
  padding: 20px;
}

.ai-cover-modal-footer {
  border-top: 1px solid #edf0f5;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.ai-cover-loading {
  color: #495057;
  font-weight: 600;
  margin-top: 18px;
}

.ai-cover-preview {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  margin-top: 18px;
  overflow: hidden;
}

.ai-cover-preview img {
  display: block;
  max-height: 360px;
  object-fit: contain;
  width: 100%;
}

@media (max-width: 575px) {
  .ai-cover-modal-backdrop {
    padding: 12px;
  }
}
</style>
